import { Client, ClientStatus } from "api/types";
import classNames from "classnames";
import Badge from "components/Badge";
import Button from "components/Button";
import GroupedMultiselect from "components/GroupedMultiselect";
import { Icon } from "components/Icon";
import { Loading } from "components/Loading";
import MultiSelectDropdown from "components/MultiSelect";
import Notification from "components/Notification";
import Pagination from "components/Pagination";
import Toggle from "components/Toggle";
import Input from "components/UnControlledInput";
import Checkbox from "components/Checkbox";
import { FieldType, FormData } from "components/types";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import {
  AUSTRALIA_STATE_OPTIONS,
  convertToMelbourneDateTimeString,
  countriesOptions,
  enumToObjects,
  languageOptions,
} from "utils/util";
import { DashboardClientsOutletContext } from ".";
import ClientDetail from "components/ClientDetail";
import { ClientsFilters, useGetClients } from "api/user";

const LevelApprovedOptions = [
  { label: "Level Approved 1", value: "level_approved_1" },
  { label: "Level Approved 2", value: "level_approved_2" },
  { label: "Level Approved 3", value: "level_approved_3" },
  { label: "Other", value: "Other" },
];

const CarePartnerOptions = [
  { label: "Care Partner (Primary)", value: "care_partner_primary" },
  { label: "Care Partner (Secondary)", value: "care_partner_secondary" },
  { label: "Care Coordinator", value: "care_coordinator" },
  { label: "Care Manager", value: "care_manager" },
  { label: "Home Care Manager", value: "home_care_manager" },
  { label: "Clinical Care Coordinator", value: "clinical_care_coordinator" },
  { label: "NDIS Plan Coordinator", value: "ndis_plan_coordinator" },
  { label: "Case Manager", value: "case_manager" },
  { label: "Family / Informal Care Partner", value: "family_informal_care_partner" },
  { label: "Agency Care Partner", value: "agency_care_partner" },
  { label: "Other", value: "other" },
];


const SupportWorkerOptions = [
  { label: "Disability Support Worker", value: "disability_support_worker" },
  { label: "Personal Care Assistant (PCA)", value: "personal_care_assistant" },
  { label: "Personal Care Worker", value: "personal_care_worker" },
  { label: "Home Care Worker", value: "home_care_worker" },
  { label: "Home Support Worker", value: "home_support_worker" },
  { label: "Community Support Worker", value: "community_support_worker" },
  { label: "Respite Support Worker", value: "respite_support_worker" },
  { label: "Senior Support Worker", value: "senior_support_worker" },
  { label: "Allied Health Assistant", value: "allied_health_assistant" },
  { label: "Enrolled Nurse (EN)", value: "enrolled_nurse" },
  { label: "Registered Nurse (RN)", value: "registered_nurse" },
  { label: "Volunteer Support Worker", value: "volunteer_support_worker" },
  { label: "Agency / Contractor Support Worker", value: "agency_support_worker" },
  { label: "Self-managed / Participant-selected Worker", value: "self_managed_worker" },
  { label: "Other", value: "other" },
];

const NurseOptions = [
  { label: "Nurse 1", value: "nurse_1" },
  { label: "Nurse 2", value: "nurse_2" },
  { label: "Nurse 3", value: "nurse_3" },
  { label: "Other", value: "Other" },
];

const ReligionOptions = [
  { label: "Christianity", value: "christianity" },
  { label: "Islam", value: "islam" },
  { label: "Hinduism", value: "hinduism" },
  { label: "Buddhism", value: "buddhism" },
  { label: "Judaism", value: "judaism" },
  { label: "Sikhism", value: "sikhism" },
  { label: "Taoism", value: "taoism" },
  { label: "Bahá'í", value: "bahai" },
  { label: "Spiritual but not religious", value: "spiritual" },
  { label: "Prefer not to say", value: "prefer_not_to_say" },
  { label: "Other", value: "other" },
];

const ServiceTypeOptions = [
  { label: "Personal Care", value: "personal_care" },
  { label: "Community Access", value: "community_access" },
  { label: "Domestic Assistance", value: "domestic_assistance" },
  { label: "Respite Care", value: "respite_care" },
  { label: "Social Support", value: "social_support" },
  { label: "Transportation", value: "transportation" },
  { label: "Allied Health Services", value: "allied_health_services" },
  { label: "Nursing Support", value: "nursing_support" },
  { label: "Behaviour Support", value: "behaviour_support" },
  { label: "Support Coordination", value: "support_coordination" },
  { label: "Plan Management", value: "plan_management" },
  { label: "Home & Garden Maintenance", value: "home_garden_maintenance" },
  { label: "Other", value: "other" },
];

const LevelAssignedOptions = [
  { label: "Level 1 - Low Support Needs", value: "level_1_low" },
  { label: "Level 2 - Moderate Support Needs", value: "level_2_moderate" },
  { label: "Level 3 - High Support Needs", value: "level_3_high" },
  { label: "Level 4 - Complex Needs", value: "level_4_complex" },
  { label: "Other", value: "other" },
];

function cleanObject(obj: any): any {
  const cleanedObj: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      let value = obj[key];

      if (typeof value === "string") {
        value = value.trim();
      }
      
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === false ||
        (Array.isArray(value) && value.length === 0)
      ) {
        continue;
      }
      
      if (value instanceof Date) {
        cleanedObj[key] = convertToMelbourneDateTimeString(value);
      } else if (Array.isArray(value)) {
        const filteredArray = value.filter(
          (item: any) => item !== null && item !== undefined && item !== "" && item !== false
        );
        if (filteredArray.length > 0) {
          cleanedObj[key] = filteredArray;
        }
      } else if (typeof value === "object" && value !== null) {
        const nested = cleanObject(value);
        if (Object.keys(nested).length > 0) {
          cleanedObj[key] = nested;
        }
      } else {
        cleanedObj[key] = value;
      }
    }
  }

  delete cleanedObj.emailCheckForValidation;
  return cleanedObj;
}

export default function AllClients() {
  const { isAdmin } = useOutletContext<DashboardClientsOutletContext>();
  const [clients, setClients] = useState<Client[]>([]);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [isEnabledArchieved, setIsEnabledArchieved] = useState(false);
  const [isError, setIsError] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(10);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const { handleSubmit, setValue, getValues, watch, reset, ...formMethods } = useForm<FormData>();

  const defaultFilter: Partial<ClientsFilters> = {
    filter_status: undefined,
    filter_level_approved: undefined,
    filter_country_of_birth: undefined,
    filter_suburb: undefined,
    filter_care_partner: undefined,
    filter_support_worker: undefined,
    filter_service_type: undefined,
    filter_level_assigned: undefined,
    filter_preferred_language: undefined,
    filter_state: undefined,
    filter_religion: undefined,
    filter_nurse: undefined,
    filter_interpreter_required: undefined,
    filter_has_spouse_within_eicare: undefined,
    filter_has_taxi_card: undefined,
    filter_aboriginal: undefined,
    filter_torres_strait_islander: undefined,
    search: undefined,
    take: "10",
    page: "1",
  };
  const [filters, setFilters] = useState({ ...defaultFilter });
  const { data, isFetching } = useGetClients(
    isAdmin,
    cleanObject({
      ...filters,
      archived_status: isEnabledArchieved ? "all" : "not_archived",
    })
  );

  useEffect(() => {
    if (data) {
      if (Array.isArray(data.clients) && data.clients) {
        setClients(data.clients);
        setTotalItems(data.count);
        return;
      }
      setIsError(true);
    }
  }, [data, isFetching]);

  const onSubmit = async (data: FormData) => {
    data["page"] = "1";
    data["take"] = itemsPerPage;
    delete data.itemPerPage;
    setFilters({ ...data });
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };
  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
    setFilters({ ...filters, page: (currentPage + 1).toString() });
  };
  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
    setFilters({ ...filters, page: (currentPage - 1).toString() });
  };

  const getBadge = (message: any, key: string) => {
    if (key === "archived") {
      return;
    }
    const isMsgBoolean = typeof message === "boolean";    
    return (
      <Badge
        message={isMsgBoolean ?
          key
          .replace(/^filter_?/, "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, c => c.toUpperCase())
        : String(message || "").split("_").join(" ")}
        key={message}
        iconName="close"
        size={16}
        className="bg-gray-100 mt-3 space-x-2"
        statusClassName="!text-gray-800 !text-sm leading-5 font-medium"
        isIconOnRight
        iconClick={() => {
          if (key === "search") {
            setValue("search", "");
          }
          // const queryParams = serializeParams(filters);
          // queryClient.invalidateQueries([`/api/user/admin/staff`, queryParams]);
          const orgValues = filters[key];
          if (
            Array.isArray(orgValues) &&
            orgValues.length >= 2 &&
            orgValues.indexOf(message) > -1
          ) {
            const updatedArray = [
              ...orgValues.filter((msg) => msg !== message),
            ];
            setFilters({
              ...filters,
              [key]: updatedArray,
            });
            return;
          }
          setFilters({ ...filters, [key]: undefined });
          setValue(key, undefined);
        }}
      />
    );
  };

  const getStatusOptions = () => {
    const statusOptions = enumToObjects(ClientStatus).map((option) => ({
      label: option.label.split("_").join(" "),
      value: option.value,
    }));
    
    const status = {
      title: "",
      options: statusOptions,
    };
    
    return [status];
  };

  const getActiveFilterValues = () => {
    const filterValues = Object.keys(
      cleanObject({
        ...filters,
        page: undefined,
        take: undefined,
        itemPerPage: undefined,
      })
    );
    if (filterValues.length > 0) {
      return (
        <div className="flex justify-between items-start mt-3">
          <div className="flex items-center flex-wrap space-x-3 w-3/4">
            <span className="text-gray-900 text-sm mt-3 font-semibold">
              Filtered by:
            </span>
            {filterValues.map((key) => {
              const message = filters[key];
              return Array.isArray(message)
                ? message.map((msg) => getBadge(msg, key))
                : typeof message === "boolean" && message === true
                  ? getBadge(message, key)
                  : getBadge((message as String) || "", key)
            })}
          </div>
          <div className="flex items-center mt-3 space-x-2 justify-end flex-nowrap w-1/4">
            <span className="text-xs leading-4 font-semibold text-gray-700">
              Enable archived accounts
            </span>
            <Toggle
              isChecked={isEnabledArchieved}
              onChange={(value) => {
                setIsEnabledArchieved(value);
                setFilters({ ...filters, archived: value });
              }}
            />
          </div>
        </div>
      );
    }
    return <></>;
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to the first page whenever items per page change
    setFilters({ ...filters, page: "1", take: newItemsPerPage.toString() });
  };

  const getNoResult = () => {
    return (
      <div className="px-6 py-14 flex flex-col items-center justify-center">
        <div>
          <Icon name="search" className="text-primary-500" size={52} />
        </div>
        <div className="mt-11 space-y-3 flex flex-col items-center justify-center mb-6">
          <span className="text-2xl leading-8 font-semibold text-gray-900">
            Sorry! No results found.
          </span>
          <span className="text-base leading-6 font-medium text-gray-500 text-center">
            We can't find any results matching your search. Try changing
            selected filters or you can enable archive if you are looking for
            older information.
          </span>
        </div>
        <Button
          variant="white"
          className="flex items-center"
          onClick={() => {
            setValue("search", "");
            reset();
            setFilters({ ...defaultFilter });
            // queryClient.invalidateQueries([`/api/user/admin/clients`]);
            setCurrentPage(1);
            setIsEnabledArchieved(false);
          }}
        >
          <Icon name="close" />
          <span className="ml-2">Clear all filters</span>
        </Button>
        {!isEnabledArchieved && (
          <div className="flex items-center mt-5 space-x-2">
            <span className="text-xs leading-4 font-semibold text-gray-700">
              Enable archived accounts
            </span>
            <Toggle
              isChecked={isEnabledArchieved}
              onChange={(value) => {
                setIsEnabledArchieved(value);
                setFilters({ ...filters, archived: value });
              }}
            />
          </div>
        )}
      </div>
    );
  };

  if (!isAdmin) {
    return <></>;
  }
  if (isError) {
    return (
      <Notification
        type="error"
        message={"Error in fetching data from server"}
      />
    );
  }

  if (!data || isFetching) {
    return <Loading />;
  }
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <span className="text-xl leading-7 font-semibold text-gray-900">
          All Clients
        </span>
        <span className="text-gray-500 text-sm">
          {`Showing ${currentPage} to ${
            currentPage * itemsPerPage
          } of ${totalItems} results`}
        </span>
      </div>
      <FormProvider
        {...{ ...formMethods, handleSubmit, setValue, getValues, reset, watch }}
      >
        <form
          className="!border-0"
          id={"filter"}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div
            className={classNames(
              "flex items-center space-x-3 justify-between"
            )}
          >
            <div className="flex items-center space-x-4 flex-1">
              <Input
                name="search"
                label=""
                type={FieldType.Input}
                containerClassName={`relative !m-0 ${showAllFilters ? "w-full" : "w-3/4"}`}
                placeholder="Search firstname, lastname, email, phone number, aged care id, medicare card number, client recipient id, referral code"
                className="pl-10"
                defaultValue={filters.search}
              >
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Icon name="search" />
                </div>
              </Input>
              {!showAllFilters && (
                <Button
                  variant="primary"
                  size="base"
                  className="space-x-2 flex justify-center items-center !px-11"
                  onClick={() =>
                    setFilters({ ...filters, search: getValues("search") })
                  }
                >
                  <Icon name="search" className="text-gray-900" />
                  <span>Search</span>
                </Button>
              )}
            </div>

            {!showAllFilters && (
              <Button
                variant="white"
                className="space-x-3"
                onClick={() => setShowAllFilters(true)}
              >
                <Icon name="filter" />
                <span>Show Filters</span>
              </Button>
            )}
            {showAllFilters && (
              <Button
                variant="white"
                className={classNames(
                  "space-x-3",
                  " outline !outline-primary-500 outline-offset-2"
                )}
                onClick={() => setShowAllFilters(false)}
              >
                <Icon name="filter" />
                <span>Hide Filters</span>
              </Button>
            )}
          </div>
          {showAllFilters && (
            <div>
              <div className="flex items-center space-x-1 md:space-x-5 flex-wrap lg:flex-nowrap">
                <GroupedMultiselect
                  name="filter_status"
                  type={FieldType.MultiSelect}
                  groupedOptions={getStatusOptions()}
                  label="Profile status"
                  className="bg-white"
                  isClearable={true}
                  defaultValue={filters.filter_status}
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[80%] max-w-[80%]"
                />
                <MultiSelectDropdown
                  name="filter_level_approved"
                  type={FieldType.MultiSelect}
                  options={LevelApprovedOptions}
                  label="Level approved"
                  isClearable={true}
                  defaultValue={filters.filter_level_approved}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_country_of_birth"
                  type={FieldType.MultiSelect}
                  options={countriesOptions}
                  label="Country of birth"
                  isClearable={true}
                  defaultValue={filters.filter_country_of_birth}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_suburb"
                  type={FieldType.MultiSelect}
                  options={languageOptions}
                  label="Suburb"
                  isClearable={true}
                  defaultValue={filters.filter_suburb}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_care_partner"
                  type={FieldType.MultiSelect}
                  options={CarePartnerOptions}
                  label="Care Partner"
                  isClearable={true}
                  defaultValue={filters.filter_care_partner}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_support_worker"
                  type={FieldType.MultiSelect}
                  options={SupportWorkerOptions}
                  label="Support worker"
                  isClearable={true}
                  defaultValue={filters.filter_support_worker}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
              </div>
              <div className="flex items-center space-x-1 md:space-x-5 flex-wrap lg:flex-nowrap">
                <MultiSelectDropdown
                  name="filter_service_type"
                  type={FieldType.MultiSelect}
                  options={ServiceTypeOptions}
                  label="Service type"
                  isClearable={true}
                  defaultValue={filters.filter_service_type}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_level_assigned"
                  type={FieldType.MultiSelect}
                  options={LevelAssignedOptions}
                  label="Level assigned"
                  isClearable={true}
                  defaultValue={filters.filter_level_assigned}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_preferred_language"
                  type={FieldType.MultiSelect}
                  options={languageOptions}
                  label="Preferred language"
                  isClearable={true}
                  defaultValue={filters.filter_preferred_language}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_state"
                  type={FieldType.MultiSelect}
                  options={AUSTRALIA_STATE_OPTIONS}
                  label="State"
                  isClearable={true}
                  defaultValue={filters.filter_state}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_nurse"
                  type={FieldType.MultiSelect}
                  options={NurseOptions}
                  label="Nurse"
                  isClearable={true}
                  defaultValue={filters.filter_nurse}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
                <MultiSelectDropdown
                  name="filter_religion"
                  type={FieldType.MultiSelect}
                  options={ReligionOptions}
                  label="Religion"
                  isClearable={true}
                  defaultValue={filters.filter_religion}
                  className="bg-white"
                  containerClassName="w-1/4 max-w-[23.5%]"
                  inputContainerClassName="w-[90%]"
                />
              </div>
              <div className="flex items-center space-x-1 md:space-x-5 flex-wrap lg:flex-nowrap mt-4">
                <Checkbox
                  name="filter_interpreter_required"
                  label="Interpreter required"
                  defaultValue={filters.filter_interpreter_required}
                  type={FieldType.Checbox}
                />
                <Checkbox
                  name="filter_has_spouse_within_eicare"
                  label="Has spouse with EI Care"
                  type={FieldType.Checbox}
                  defaultValue={filters.filter_has_spouse_within_eicare}
                />
                <Checkbox
                  name="filter_has_taxi_card"
                  label="Has Taxi card"
                  type={FieldType.Checbox}
                  defaultValue={filters.filter_has_taxi_card}
                />
                <Checkbox
                  name="filter_aboriginal"
                  label="Aboriginal"
                  type={FieldType.Checbox}
                  defaultValue={filters.filter_aboriginal}
                />
                <Checkbox
                  name="filter_torres_strait_islander"
                  label="Torres Strait Islander (TSI)"
                  type={FieldType.Checbox}
                  defaultValue={filters.filter_torres_strait_islander}
                />
              </div>
              <div className="flex items-center space-x-3 justify-between mt-5">
                <Button
                  variant="primary"
                  type="submit"
                  form="filter"
                  className="space-x-2 flex justify-center items-center !px-11"
                >
                  <Icon name="search" className="text-gray-900" />
                  <span>Search</span>
                </Button>
                <Button
                  variant="gray"
                  className="flex items-center"
                  onClick={() => {
                    setValue("search", "");
                    reset();
                    setFilters({ ...defaultFilter });
                    // queryClient.invalidateQueries([`/api/user/admin/clients`]);
                    setCurrentPage(1);
                    setIsEnabledArchieved(false);
                  }}
                >
                  <Icon name="close" />
                  <span className="ml-2">Clear all filters</span>
                </Button>
              </div>
            </div>
          )}
          {getActiveFilterValues()}
        </form>
      </FormProvider>
      <div className="w-full mt-1">
        {clients && clients.length > 0
          ? clients.map((client) => (
              <ClientDetail client={client} key={client.id} />
            ))
          : getNoResult()}
      </div>
      <FormProvider
        {...{ ...formMethods, handleSubmit, setValue, getValues, reset, watch }}
      >
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
        />
      </FormProvider>
    </>
  );
}
